import { API_BASE_URL, ApiResponse } from '../config/supabase';

export interface CreateOrderRequest {
  // User details (person making the purchase)
  user_name: string;
  user_mobile: string;
  user_email?: string;
  
  // Devotee details (for whom the donation is made)
  devotee_name: string;
  devotee_phone: string;
  
  // Order details
  metal_type: 'gold' | 'silver' | 'platinum';
  quantity_grams: number;
  amount_inr: number;
  rate_per_gram: number;
  block_id: string;
  payment_method: string;
}

export interface OrderResponse {
  success: boolean;
  data?: {
    transaction: any;
    augmont_order: {
      order_id: string;
      order_status: string;
      payment_url?: string;
      order_details: any;
    };
  };
  error?: string;
}

export interface OrderStatusResponse {
  success: boolean;
  data?: {
    order_id: string;
    order_status: string;
    payment_url?: string;
    order_details: {
      invoice_number: string;
      transaction_id: string;
      amount: number;
      quantity: number;
      rate: number;
    };
  };
  error?: string;
}

class AugmontOrderService {
  private baseUrl = `${API_BASE_URL}/augmont-orders`;

  async createOrder(orderData: CreateOrderRequest): Promise<OrderResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });
      
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to create order');
      }
      
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create order'
      };
    }
  }

  async getOrderStatus(orderId: string): Promise<OrderStatusResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/${orderId}/status`);
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get order status');
      }
      
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get order status'
      };
    }
  }

  async getOrders(): Promise<{ success: boolean; data?: any[]; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/list`);
      const result: ApiResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to get orders');
      }
      
      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get orders'
      };
    }
  }
}

export const augmontOrderService = new AugmontOrderService(); 